import React from 'react';
import PropTypes from 'prop-types';
import { Redirect, useLocation } from 'react-router-dom';
import { Spin, Alert } from 'antd';
import { useAuth } from '../contexts/AuthContext';

/**
 * ProtectedRoute component for authentication and role-based access control
 */
const ProtectedRoute = ({ 
  children, 
  requiredRoles = [], 
  fallbackPath = '/auth',
  showAccessDenied = true 
}) => {
  const { user, isAuthenticated, isLoading, error } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column'
      }}>
        <Spin size="large" />
        <div style={{ marginTop: '20px' }}>
          Authenticating...
        </div>
      </div>
    );
  }

  // Show error if authentication failed
  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        padding: '20px'
      }}>
        <Alert
          message="Authentication Error"
          description={error}
          type="error"
          showIcon
          style={{ maxWidth: '500px' }}
        />
      </div>
    );
  }

  // If not authenticated, redirect to auth page
  if (!isAuthenticated || !user) {
    return (
      <Redirect 
        to={{
          pathname: fallbackPath,
          state: { from: location }
        }} 
      />
    );
  }

  // If no roles required, allow access
  if (!requiredRoles || requiredRoles.length === 0) {
    return children;
  }

  // Check role permissions
  const userRoles = user.roles || [];
  const userRoleArray = Array.isArray(userRoles) ? userRoles : [userRoles];
  
  const hasAccess = userRoleArray.some(role => 
    requiredRoles.includes(role) || requiredRoles.includes('*')
  );
  console.log(user);
  console.log(requiredRoles);
  console.log(hasAccess);
  if (!hasAccess) {
    if (showAccessDenied) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          padding: '20px'
        }}>
          <Alert
            message="Access Denied"
            description="You don't have permission to access this page. Please contact your administrator."
            type="error"
            showIcon
            style={{ maxWidth: '500px' }}
          />
        </div>
      );
    }
    
    return (
      <Redirect 
        to={{
          pathname: fallbackPath,
          state: { from: location }
        }} 
      />
    );
  }

  // User has access, render children
  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requiredRoles: PropTypes.arrayOf(PropTypes.string),
  fallbackPath: PropTypes.string,
  showAccessDenied: PropTypes.bool
};

ProtectedRoute.defaultProps = {
  requiredRoles: [],
  fallbackPath: '/auth',
  showAccessDenied: true
};

export default ProtectedRoute;